/**
 * Backend-lines counterpart to `bucketedRuns.query.ts`. See that file's header
 * for the rationale around the top-level pinned-runs alias and the lingering
 * legacy `*_INTERNAL_ID` query that still relies on
 * `BACKEND_LINES_DELTA_QUERY_LEGACY_FRAGMENT`.
 */
import gql from 'graphql-tag';

import {LINE_PLOT_RUN_FRAGMENT} from './linePlotRun.fragment';

/**
 * Shared selection on BucketDiffs used by both the main `project.runs.deltas`
 * and the top-level `pinnedRuns: runs(ids:).deltas` selection. Keeping this
 * separate means the inner `delta { op run { ... } }` payload is defined once.
 */
export const BACKEND_LINES_DELTAS_FRAGMENT = gql`
  fragment BackendLinesDeltasFragment on BucketDiffs {
    order
    incomplete
    delta {
      op
      run {
        ...LinePlotRunFragment
        config(keys: $configKeys) @include(if: $enableConfig)
        defaultColorIndex
        displayName
        id
        lines(
          specs: $bucketedHistorySpecs
          excludeOutliers: $excludeOutliers
          primaryLineType: $primaryLineType
        )
        name
        projectId
        summaryMetrics(keys: $summaryKeys) @include(if: $enableSummary)
        updatedAt
      }
    }
  }
  ${LINE_PLOT_RUN_FRAGMENT}
`;

export const BACKEND_LINES_RUN_DELTAS_FRAGMENT = gql`
  fragment BackendLinesRunDeltasFragment on RunConnection {
    deltas(
      currentRuns: $currentRuns
      lastUpdated: $lastUpdated
      timeout: $timeout
    ) {
      ...BackendLinesDeltasFragment
    }
  }
  ${BACKEND_LINES_DELTAS_FRAGMENT}
`;

export const BACKEND_LINES_DELTA_QUERY_FRAGMENT = gql`
  fragment BackendLinesDeltaQueryFragment on Project {
    entityName
    name
    runs(
      first: $limit
      order: $order
      filters: $filters
      groupKeys: $groupKeys
      groupLevel: $groupLevel
    ) {
      ...BackendLinesRunDeltasFragment
    }
  }
  ${BACKEND_LINES_RUN_DELTAS_FRAGMENT}
`;

const BACKEND_LINES_DELTA_QUERY_LEGACY_FRAGMENT = gql`
  fragment BackendLinesDeltaQueryLegacyFragment on Project {
    entityName
    name
    pinnedRuns: runs(filters: $pinnedRunFilters, first: $pinnedRunsLimit)
      @include(if: $includePinnedRuns) {
      ...BackendLinesRunDeltasFragment
    }
    runs(
      first: $limit
      order: $order
      filters: $filters
      groupKeys: $groupKeys
      groupLevel: $groupLevel
    ) {
      ...BackendLinesRunDeltasFragment
    }
  }
  ${BACKEND_LINES_RUN_DELTAS_FRAGMENT}
`;

export const BACKEND_LINES_DELTA_QUERY = gql`
  # Inlined literal — codegen cannot resolve template-string interpolations.
  # See bucketedData/queries/architecture.md §11 (GraphQL Operation Name Convention).
  query BackendLinesDeltaQuery_DD_DQ_U_BLDQ(
    $bucketedHistorySpecs: [JSONString!]!
    $configKeys: [String!]
    $currentRuns: [String!]!
    $enableConfig: Boolean = false
    $enableSummary: Boolean = false
    $entityName: String!
    $excludeOutliers: Boolean
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $includePinnedRuns: Boolean = false
    $lastUpdated: DateTime!
    $limit: Int!
    $order: String!
    $pinnedRunIds: [ID!]!
    $projectName: String!
    $summaryKeys: [String!]
    $timeout: Int
    $primaryLineType: String
  ) {
    pinnedRuns: runs(ids: $pinnedRunIds) @include(if: $includePinnedRuns) {
      deltas(
        currentRuns: $currentRuns
        lastUpdated: $lastUpdated
        timeout: $timeout
      ) {
        ...BackendLinesDeltasFragment
      }
    }
    project(name: $projectName, entityName: $entityName) {
      id
      ...BackendLinesDeltaQueryFragment
    }
  }
  ${BACKEND_LINES_DELTAS_FRAGMENT}
  ${BACKEND_LINES_DELTA_QUERY_FRAGMENT}
`;

export const BACKEND_LINES_DELTA_QUERY_INTERNAL_ID = gql`
  # Inlined literal — codegen cannot resolve template-string interpolations.
  # See bucketedData/queries/architecture.md §11 (GraphQL Operation Name Convention).
  query BackendLinesInternalIdDeltaQuery_DD_DQ_U_BLDQ(
    $bucketedHistorySpecs: [JSONString!]!
    $configKeys: [String!]
    $currentRuns: [String!]!
    $enableConfig: Boolean = false
    $enableSummary: Boolean = false
    $excludeOutliers: Boolean
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $includePinnedRuns: Boolean = false
    $internalId: ID!
    $lastUpdated: DateTime!
    $limit: Int!
    $order: String!
    $pinnedRunFilters: JSONString
    $pinnedRunsLimit: Int!
    $summaryKeys: [String!]
    $timeout: Int
    $primaryLineType: String
  ) {
    project(internalId: $internalId) {
      id
      ...BackendLinesDeltaQueryLegacyFragment
    }
  }
  ${BACKEND_LINES_DELTA_QUERY_LEGACY_FRAGMENT}
`;
